import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { FormControl } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { IStorage, LocalStorage } from '@isophi/core-legacy';
import { ToastService } from '@isophi/core-ui';
import {
  ChildrenService,
  FieldTypeEnum,
  HistoryChangeItem,
  HistoryModelTypeEnum,
  HistoryRecordDateUpdate,
  HistoryRecordDelete,
  HistoryRecordFieldValueUpdate,
  HistoryRecordFutureValueReplace,
  UnifiedChildHistoryService,
} from '@isophi/mng-api';
import { TranslocoService } from '@jsverse/transloco';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { NgxSpinnerService } from 'ngx-spinner';
import { debounceTime, distinctUntilChanged, firstValueFrom, Observable, Subject, takeUntil } from 'rxjs';

import { RouterLinkFactory } from '../../../../core/router/router-link.factory';
import { HelperService } from '../../../../core/services/helper.service';
import { LocalStorageConstService } from '../../../../shared/services/local-storage-const.service';
import { HistoryDateEditModalComponent } from './history-date-edit-modal/history-date-edit-modal.component';
import { HistoryDeleteConfirmModalComponent } from './history-delete-confirm-modal/history-delete-confirm-modal.component';
import { HistoryReplaceConfirmModalComponent } from './history-replace-confirm-modal/history-replace-confirm-modal.component';
import { HistoryValueEditModalComponent } from './history-value-edit-modal/history-value-edit-modal.component';

type HistoryValue = string | number | boolean | Record<string, unknown> | Array<unknown> | null | undefined;

interface ChildHistoryRow {
  historyModelType: HistoryModelTypeEnum;
  recordId: string;
  isCurrent: boolean;
  isLocked: boolean;
  validFrom: string;
  fieldName: string;
  fieldDisplayText?: string;
  fieldType: FieldTypeEnum;
  fieldOptions?: Array<{ value: unknown; label: string }>;
  oldValue?: HistoryValue;
  newValue?: HistoryValue;
  oldValueDisplayText?: HistoryValue;
  newValueDisplayText?: HistoryValue;
  firstFutureValue?: HistoryValue;
  firstFutureValueDisplayText?: HistoryValue;
  firstFutureValueDate?: Date;
}

@Component({
  selector: 'app-child-profile-history',
  templateUrl: './child-profile-history.component.html',
  styleUrls: ['./child-profile-history.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class ChildProfileHistoryComponent implements OnInit, OnDestroy {
  childUuid: string;
  private readonly destroy$ = new Subject<void>();

  rows: ChildHistoryRow[] = [];
  totalCount = 0;

  page = 1;
  pageSize = 100;

  searchControl = new FormControl('', { nonNullable: true });

  private readonly localStorage: IStorage = new LocalStorage();

  constructor(
    public links: RouterLinkFactory,
    private route: ActivatedRoute,
    private helperService: HelperService,
    private spinner: NgxSpinnerService,
    private cdr: ChangeDetectorRef,
    private translocoService: TranslocoService,
    private toastr: ToastService,
    private modal: NgbModal,
    private unifiedChildHistoryService: UnifiedChildHistoryService,
    private childrenService: ChildrenService,
    private lSConstService: LocalStorageConstService
  ) {}

  ngOnInit(): void {
    this.childUuid = this.route.snapshot.paramMap.get('id');
    this.helperService.setTitle(null, this.translocoService.translate('childHistory/title'));
    const savedLimit = this.localStorage.getItem(this.lSConstService.childProfileHistoryPageLimit);
    if (savedLimit) {
      this.pageSize = +savedLimit;
    }

    this.searchControl.valueChanges.pipe(debounceTime(350), distinctUntilChanged(), takeUntil(this.destroy$)).subscribe(() => {
      this.page = 1;
      void this.loadHistory();
    });

    void this.loadHistory();
    this.loadChildName();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.helperService.clearSmallTextTitle();
  }

  onPageChange(page: number): void {
    this.page = page;
    void this.loadHistory();
  }

  onLimitChange(value: number): void {
    this.pageSize = value;
    this.page = 1;
    this.localStorage.setItem(this.lSConstService.childProfileHistoryPageLimit, String(value));
    this.loadHistory();
  }

  modelLabelKey(row: ChildHistoryRow): string {
    switch (row.historyModelType) {
      case HistoryModelTypeEnum.ChildSupportMeasures:
        return 'childHistory/modelSupportMeasure';
      case HistoryModelTypeEnum.ChildSpzRecommendation:
        return 'childHistory/modelSpzRecommendation';
      case HistoryModelTypeEnum.ChildProfileAddress:
        return 'childHistory/modelAddress';
      default:
        return 'childHistory/modelChildren';
    }
  }

  valueToString(value: HistoryValue): string {
    if (value === null || value === undefined || value === '') {
      return '-';
    }

    if (typeof value === 'object') {
      return JSON.stringify(value);
    }

    return `${value}`;
  }

  isFirstInGroup(index: number): boolean {
    return index === 0 || this.rows[index].recordId !== this.rows[index - 1].recordId;
  }

  fieldNameToLabel(row: ChildHistoryRow): string {
    if (row.fieldDisplayText) {
      return row.fieldDisplayText;
    }

    if (!row.fieldName) {
      return '-';
    }

    return row.fieldName
      .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
      .replace(/[_.-]+/g, ' ')
      .trim();
  }

  removeRow(row: ChildHistoryRow): void {
    if (row.isLocked) {
      this.toastr.error(this.translocoService.translate('childHistory/lockedActionError'));
      return;
    }

    const modalRef = this.modal.open(HistoryReplaceConfirmModalComponent, { size: 'md', centered: true });
    modalRef.componentInstance.changeSummary = [
      { label: this.translocoService.translate('childHistory/field'), value: this.fieldNameToLabel(row) },
      {
        label: this.translocoService.translate('childHistory/currentValue'),
        value: this.shortValueSummary(row.newValueDisplayText ?? row.newValue),
      },
      {
        label: this.translocoService.translate('childHistory/futureValue'),
        value: this.shortValueSummary(row.firstFutureValueDisplayText ?? row.firstFutureValue),
      },
      {
        label: this.translocoService.translate('childHistory/futureValueDate'),
        value: row.firstFutureValueDate.toLocaleDateString(),
      },
    ];

    modalRef.result
      .then(async (confirmed) => {
        if (!confirmed) {
          return;
        }

        try {
          this.spinner.show();
          await firstValueFrom(this.revertRow$(row));
          this.toastr.success(this.translocoService.translate('childHistory/replaceSuccess'));
          await this.loadHistory();
        } catch (error) {
          this.toastr.error(this.extractApiErrorMessage(error, 'childHistory/replaceError'));
        } finally {
          this.spinner.hide();
        }
      })
      .catch(() => null);
  }

  editRowDate(row: ChildHistoryRow): void {
    if (row.isLocked) {
      this.toastr.error(this.translocoService.translate('childHistory/lockedActionError'));
      return;
    }

    const modalRef = this.modal.open(HistoryDateEditModalComponent, { size: 'lg', centered: true });
    modalRef.componentInstance.validFrom = row.validFrom;
    modalRef.componentInstance.fieldLabel = this.fieldNameToLabel(row);

    modalRef.result
      .then(async (validFrom: string) => {
        if (!validFrom) {
          return;
        }

        try {
          this.spinner.show();
          await firstValueFrom(this.updateRowDate$(row, validFrom));
          this.toastr.success(this.translocoService.translate('childHistory/editDateSuccess'));
          await this.loadHistory();
        } catch (error) {
          this.toastr.error(this.extractApiErrorMessage(error, 'childHistory/editDateError'));
        } finally {
          this.spinner.hide();
        }
      })
      .catch(() => null);
  }

  editRowValue(row: ChildHistoryRow): void {
    if (row.isLocked || row.isCurrent) {
      return;
    }

    const modalRef = this.modal.open(HistoryValueEditModalComponent, { size: 'md', centered: true });
    modalRef.componentInstance.fieldLabel = this.fieldNameToLabel(row);
    modalRef.componentInstance.fieldType = row.fieldType;
    modalRef.componentInstance.fieldOptions = row.fieldOptions ?? [];
    modalRef.componentInstance.currentValue = row.newValue;
    modalRef.componentInstance.validFrom = row.validFrom;

    modalRef.result
      .then(async (result: { newValue: unknown } | undefined) => {
        if (!result) {
          return;
        }

        try {
          this.spinner.show();
          await firstValueFrom(this.updateRowValue$(row, result.newValue));
          this.toastr.success(this.translocoService.translate('childHistory/editValueSuccess'));
          await this.loadHistory();
        } catch (error) {
          this.toastr.error(this.extractApiErrorMessage(error, 'childHistory/editValueError'));
        } finally {
          this.spinner.hide();
          this.cdr.markForCheck();
        }
      })
      .catch(() => null);
  }

  deleteRow(row: ChildHistoryRow): void {
    if (row.isLocked || row.isCurrent) {
      return;
    }

    const modalRef = this.modal.open(HistoryDeleteConfirmModalComponent, { size: 'md', centered: true });
    modalRef.componentInstance.fieldLabel = this.fieldNameToLabel(row);
    modalRef.componentInstance.value = this.shortValueSummary(row.newValueDisplayText ?? row.newValue);
    modalRef.componentInstance.validFrom = this.formatDate(row.validFrom);

    modalRef.result
      .then(async (confirmed) => {
        if (!confirmed) {
          return;
        }

        try {
          this.spinner.show();
          await firstValueFrom(this.deleteRow$(row));
          this.toastr.success(this.translocoService.translate('childHistory/deleteSuccess'));
          await this.loadHistory();
        } catch (error) {
          this.toastr.error(this.extractApiErrorMessage(error, 'childHistory/deleteError'));
        } finally {
          this.spinner.hide();
          this.cdr.markForCheck();
        }
      })
      .catch(() => null);
  }

  private async loadHistory(): Promise<void> {
    if (!this.childUuid) {
      this.rows = [];
      this.totalCount = 0;
      return;
    }

    try {
      this.spinner.show();
      const searchTerm = this.searchControl.value.trim() || undefined;
      const response = await firstValueFrom(
        this.unifiedChildHistoryService.unifiedChildHistoryList(
          this.childUuid,
          undefined,
          this.pageSize,
          (this.page - 1) * this.pageSize,
          searchTerm
        )
      );

      this.totalCount = response?.count || 0;
      this.rows = this.mapRows(response?.results || []);
    } finally {
      this.spinner.hide();
      this.cdr.markForCheck();
    }
  }

  private loadChildName(): void {
    if (!this.childUuid) {
      return;
    }

    this.childrenService
      .childrenRetrieve(this.childUuid)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (child) => {
          const name = `${child.firstName} ${child.lastName}`;
          this.helperService.setTitle(null, this.translocoService.translate('childHistory/titleWithName', { name }));
        },
        // Title stays generic when the child can't be loaded
        error: () => null,
      });
  }

  private mapRows(rows: HistoryChangeItem[]): ChildHistoryRow[] {
    return rows
      .filter((item) => !!item?.recordId)
      .map((item) => ({
        historyModelType: item.historyModelType,
        recordId: item.recordId,
        isCurrent: item.isCurrent,
        isLocked: item.isLocked,
        validFrom: item.validFrom,
        fieldName: item.fieldName,
        fieldDisplayText: item.fieldDisplayText,
        fieldType: item.fieldType,
        fieldOptions: item.fieldOptions,
        oldValue: item.oldValue,
        newValue: item.newValue,
        oldValueDisplayText: item.oldValueDisplayText,
        newValueDisplayText: item.newValueDisplayText,
        firstFutureValue: item.firstFutureValue,
        firstFutureValueDisplayText: item.firstFutureValueDisplayText,
        firstFutureValueDate: item.firstFutureValueDate ? new Date(item.firstFutureValueDate) : null,
      }));
  }

  private revertRow$(row: ChildHistoryRow): Observable<unknown> {
    const payload: HistoryRecordFutureValueReplace = {
      recordId: row.recordId,
      historyModelType: row.historyModelType,
      fieldName: row.fieldName,
    };

    return this.unifiedChildHistoryService.unifiedChildHistoryApplyFutureValueCreate(payload, this.childUuid);
  }

  private updateRowDate$(row: ChildHistoryRow, validFrom: string): Observable<unknown> {
    const payload: HistoryRecordDateUpdate = {
      recordId: row.recordId,
      historyModelType: row.historyModelType,
      fieldName: row.fieldName,
      validFrom,
    };

    return this.unifiedChildHistoryService.unifiedChildHistoryUpdateDateCreate(payload, this.childUuid);
  }

  private deleteRow$(row: ChildHistoryRow): Observable<unknown> {
    const payload: HistoryRecordDelete = {
      recordId: row.recordId,
      historyModelType: row.historyModelType,
      fieldName: row.fieldName,
    };

    return this.unifiedChildHistoryService.unifiedChildHistoryDeleteRecordCreate(payload, this.childUuid);
  }

  private updateRowValue$(row: ChildHistoryRow, newValue: unknown): Observable<unknown> {
    const payload: HistoryRecordFieldValueUpdate = {
      recordId: row.recordId,
      historyModelType: row.historyModelType,
      fieldName: row.fieldName,
      newValue,
    };

    return this.unifiedChildHistoryService.unifiedChildHistoryUpdateFieldCreate(payload, this.childUuid);
  }

  private formatDate(value: string): string {
    if (!value) {
      return '';
    }
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) {
      return value;
    }
    return `${d.getDate()}. ${d.getMonth() + 1}. ${d.getFullYear()}`;
  }

  private shortValueSummary(value: HistoryValue): string {
    const text = this.valueToString(value);
    const maxLength = 120;

    if (text.length <= maxLength) {
      return text;
    }

    return `${text.slice(0, maxLength)}...`;
  }

  private extractApiErrorMessage(error: unknown, fallbackTranslationKey: string): string {
    const fallback = this.translocoService.translate(fallbackTranslationKey);
    const responseError = (error as { error?: unknown })?.error;
    const payload = responseError ?? error;

    if (typeof payload === 'string' && payload.trim()) {
      return payload;
    }

    if (!payload || typeof payload !== 'object') {
      return fallback;
    }

    const values = Object.values(payload as Record<string, unknown>);

    for (const value of values) {
      if (typeof value === 'string' && value.trim()) {
        return value;
      }

      if (Array.isArray(value) && value.length > 0) {
        const first = value[0];
        if (typeof first === 'string' && first.trim()) {
          return first;
        }
      }
    }

    return fallback;
  }
}
